import htmx from 'htmx.org'
import { rewriteLpLinks, rewriteFormUtmInput } from './rewrite_lp'
import { trackAction } from './ab_track'
import { bindPopularArticleClickTracking } from './popular_article_click_tracking'
import { initReviewHelpfulButtons } from './reform_review_helpful/button'
import { lpTypeOf } from './utils'

// htmx は全ページ共通でここから読み込む。AB の swap-trigger（不可視トリガー→partial）は
// htmx に依存するため、一部バンドルだけでなく全ページで使えるようにする。
window.htmx = htmx

// 全ページ共通で読み込むトラッキングの起点。
// 流入経路のutm引き継ぎ・ABテストの utm_campaign マーカー適用と、LP遷移の計測を行う。
// 今後の共通トラッキング（スクロール計測など）もここに集約していく。各メインバンドルに
// 散らばらせず全ページ無条件に読み込むことで、バンドルが増えても追従不要にする。
// LPリンク・フォームの書き換え完了を通知するフック点。書き換えの「後」に手を入れたい処理
// （ABテストによる遷移先パスの乗せ替え等）が、貼り直しのたびにフックできるようにする。
export const LP_REWRITE_EVENT = 'hapisumu:afterLpRewrite'

export function initTracking() {
  rewriteLpLinks()
  rewriteFormUtmInput()
  document.dispatchEvent(new CustomEvent(LP_REWRITE_EVENT))
}

// ページ内で「画面の下端がコンテンツのどこまで到達したか」(B定義)の最大到達率(0-100%)と、
// 最大スクロール量(px)を保持する。scroll は passive リスナーで購読し、最大値だけを更新する。
let maxScrollDepth = 0
let maxScrollPx = 0
const trackedCompanyDetailsOpenElements = new WeakSet()
const COMPANY_DETAILS_OPEN_TRACKING_INITIALIZED = '__hapisumuCompanyDetailsOpenTrackingInitialized'

function currentScrollDepth() {
  const scrollHeight = document.documentElement.scrollHeight
  if (scrollHeight <= 0) return 100
  // ビューポート下端の位置 / ページ全体高。1画面に収まるページは最初から100%（全部見えている）。
  return Math.min(100, Math.round(((window.scrollY + window.innerHeight) / scrollHeight) * 100))
}

function recordScroll() {
  const depth = currentScrollDepth()
  if (depth > maxScrollDepth) maxScrollDepth = depth
  if (window.scrollY > maxScrollPx) maxScrollPx = window.scrollY
}

window.addEventListener('scroll', recordScroll, { passive: true })

// 計測時点のエンゲージメント（画面サイズ・最大スクロール到達）のスナップショットを返す。
function engagementContext() {
  recordScroll()
  return {
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    scroll_depth: maxScrollDepth,
    max_scroll_px: maxScrollPx,
    scroll_height: document.documentElement.scrollHeight,
  }
}

function normalizedText(element) {
  return (element?.textContent || '').replace(/\s+/g, ' ').trim()
}

function trackCompanyDetailsOpen(element, labelElement = element) {
  if (!(element instanceof HTMLElement)) return
  if (trackedCompanyDetailsOpenElements.has(element)) return

  const section = element.dataset.trackCompanyDetailsOpen
  if (!section) return

  trackedCompanyDetailsOpenElements.add(element)
  trackAction('company_details_open', {
    section,
    label: normalizedText(labelElement),
    path: window.location.pathname,
  })
}

// utm: { campaign, content, medium, source, term } を受け取り lp_transition を記録する。
// medium = 掲載面（リンク種別）、source = 流入区分、term = 記事slug。
function trackLpTransition(kind, lp, utm) {
  trackAction('lp_transition', {
    kind,
    lp,
    campaign: utm.campaign || '',
    content: utm.content || '',
    medium: utm.medium || '',
    source: utm.source || '',
    term: utm.term || '',
    path: window.location.pathname,
    ...engagementContext(),
  })
}

// LPフォーム（GET）の送信を計測する。送信される hidden input の utm 値（書き換え後）を拾う。
function handleLpFormSubmit(event) {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return
  if (!form.classList.contains('reform-lp-form')) return

  const lp = lpTypeOf(form.action)
  if (!lp) return

  const value = (name) => form.querySelector(`input[name="${name}"]`)?.value || ''
  trackLpTransition('form', lp, {
    campaign: value('utm_campaign'),
    content: value('utm_content'),
    medium: value('utm_medium'),
    source: value('utm_source'),
    term: value('utm_term'),
  })
}

// LPリンクのクリックを計測する。
function handleLpLinkClick(event) {
  if (!(event.target instanceof Element)) return
  const link = event.target.closest('a')
  if (!link) return

  const lp = lpTypeOf(link.href)
  if (!lp) return

  const params = new URL(link.href, window.location.origin).searchParams
  trackLpTransition('link', lp, {
    campaign: params.get('utm_campaign'),
    content: params.get('utm_content'),
    medium: params.get('utm_medium'),
    source: params.get('utm_source'),
    term: params.get('utm_term'),
  })
}

document.addEventListener('submit', handleLpFormSubmit)
document.addEventListener('click', handleLpLinkClick)
bindPopularArticleClickTracking()
initReviewHelpfulButtons()

function handleCompanyDetailsOpen(event) {
  if (event.type === 'click' && event.target instanceof Element) {
    const toggle = event.target.closest('[data-track-company-details-open][data-clamp-toggle]')
    if (toggle instanceof HTMLElement && toggle.getAttribute('aria-expanded') === 'true') {
      trackCompanyDetailsOpen(toggle)
    }
  } else if (event.target instanceof HTMLDetailsElement && event.target.open) {
    trackCompanyDetailsOpen(event.target, event.target.querySelector('summary'))
  }
}

function bindCompanyDetailsOpenTracking() {
  if (window[COMPANY_DETAILS_OPEN_TRACKING_INITIALIZED]) return

  window[COMPANY_DETAILS_OPEN_TRACKING_INITIALIZED] = true
  document.addEventListener('click', handleCompanyDetailsOpen)
  document.addEventListener('toggle', handleCompanyDetailsOpen, true)
}

bindCompanyDetailsOpenTracking()

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTracking)
} else {
  initTracking()
}

// htmx リクエスト後にトラッキングを再適用する。afterSwap ではなく afterRequest を購読するのは、
// ABテストの control が partial で 204 を返す（htmx は swap せず afterSwap も発火しない）ため。
// 204 でもサーバはマーカー Cookie を発行済みなので、既に描画済みのフォーム・リンクにも反映する必要がある。
// afterRequest は swap の有無に関わらず発火し、document までバブルする。
document.addEventListener('htmx:afterRequest', initTracking)

// ページ離脱時に、そのページビューでの最大スクロール到達を記録する（遷移しなかった訪問も含む）。
// impression はサーバ側でレンダリング時に記録されるためスクロールを持てないので、別アクションで補う。
// visibilitychange→hidden はモバイルでも確実に発火する（pagehide/beforeunload は取りこぼす）。
// タブ切替で複数回 hidden になりうるため、最大到達率が前回送信時より伸びた時だけ送る。
let lastSentScrollDepth = -1

function trackPageLeave() {
  recordScroll()
  if (maxScrollDepth <= lastSentScrollDepth) return
  lastSentScrollDepth = maxScrollDepth
  trackAction('page_leave', {
    path: window.location.pathname,
    ...engagementContext(),
  })
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') trackPageLeave()
})
